import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Check, AlertTriangle, HelpCircle, Lock, Clock, Shield, Eye, UserCheck, Briefcase, Users2, Download, FileText, DollarSign } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { formatDate, formatTime, formatDuration } from '../hooks/useSupabase'
import { Button, Card, Badge, Textarea, Toast } from '../components/ui/SharedUI'

const CONFIDENCE_MAP = {
  documented: { short: 'Documented', color: '#005440', bg: '#E1F5EE' },
  pattern: { short: 'Pattern-based', color: '#2C7A7B', bg: '#EDF7F4' },
  recollection: { short: 'From memory', color: '#8b5000', bg: '#FCF5E8' },
}
const CONFIDENCE_CSV_LABEL = { documented: 'Documented', pattern: 'Pattern-based', recollection: 'From memory' }

const SHARE_TYPE_META = {
  verification: { label: 'Shift verification', icon: UserCheck },
  oversight: { label: 'Care oversight', icon: Eye },
  professional: { label: 'Professional review', icon: Briefcase },
  coordination: { label: 'Care team coordination', icon: Users2 },
}

export function SharedViewPage() {
  const { token } = useParams()
  const [share, setShare] = useState(null)
  const [shifts, setShifts] = useState([])
  const [notes, setNotes] = useState([])
  const [rates, setRates] = useState(null)
  const [caregiverProfile, setCaregiverProfile] = useState(null)
  const [corroborations, setCorroborations] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [respondingTo, setRespondingTo] = useState(null)
  const [responseNote, setResponseNote] = useState('')

  // Email verification gate
  const [verifyingEmail, setVerifyingEmail] = useState('')
  const [emailVerified, setEmailVerified] = useState(false)
  const [emailError, setEmailError] = useState(null)

  const [toast, setToast] = useState(null)

  useEffect(() => { loadShare() }, [token])

  async function loadShare() {
    setLoading(true)
    try {
      const { data: shareData, error: shareErr } = await supabase
        .from('shares')
        .select('*')
        .eq('token', token)
        .single()

      if (shareErr || !shareData) { setError('invalid'); return }
      if (shareData.revoked_at) { setError('revoked'); return }
      if (shareData.expires_at && new Date(shareData.expires_at) < new Date()) { setError('expired'); return }

      setShare(shareData)

      // Track access
      await supabase
        .from('shares')
        .update({ last_accessed_at: new Date().toISOString() })
        .eq('id', shareData.id)

      // Fetch caregiver profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('name, role')
        .eq('id', shareData.caregiver_id)
        .single()
      setCaregiverProfile(profile)

      // Fetch shifts
      let shiftQuery = supabase
        .from('shifts')
        .select('*')
        .eq('caregiver_id', shareData.caregiver_id)
        .eq('is_active', false)
        .not('clock_out', 'is', null)
        .order('clock_in', { ascending: false })
        .limit(100)

      if (shareData.recipient_id) shiftQuery = shiftQuery.eq('recipient_id', shareData.recipient_id)

      const { data: shiftsData } = await shiftQuery
      setShifts(shiftsData || [])

      // Fetch evidence notes (oversight + professional only)
      if (['oversight', 'professional'].includes(shareData.share_type)) {
        const { data: notesData } = await supabase
          .from('evidence_notes')
          .select('*')
          .eq('caregiver_id', shareData.caregiver_id)
          .order('created_at', { ascending: false })
          .limit(50)
        setNotes(notesData || [])
      }

      // Fetch rates (professional only)
      if (shareData.share_type === 'professional') {
        const { data: ratesData } = await supabase
          .from('rates')
          .select('*')
          .eq('caregiver_id', shareData.caregiver_id)
          .single()
        setRates(ratesData)
      }

      // Fetch corroborations for shifts
      if (shiftsData?.length > 0) {
        const shiftIds = shiftsData.map(s => s.id)
        const { data: corrobData } = await supabase
          .from('corroborations')
          .select('*')
          .in('shift_id', shiftIds)
          .order('created_at', { ascending: false })

        const grouped = {}
        for (const c of (corrobData || [])) {
          if (!grouped[c.shift_id]) grouped[c.shift_id] = []
          grouped[c.shift_id].push(c)
        }
        setCorroborations(grouped)
      }

      // If email was already verified previously, skip the gate
      if (shareData.email_verified_at && shareData.shared_with_email) {
        setEmailVerified(true)
      }
    } catch (err) {
      setError('unknown')
    } finally {
      setLoading(false)
    }
  }

  async function verifyEmail(e) {
    e.preventDefault()
    setEmailError(null)

    // Case-insensitive email comparison
    const normalizedInput = verifyingEmail.trim().toLowerCase()
    const normalizedShareEmail = (share.shared_with_email || '').trim().toLowerCase()

    if (!normalizedShareEmail) {
      setEmailError('This share does not have a required email. Please contact the caregiver to add one.')
      return
    }

    if (normalizedInput !== normalizedShareEmail) {
      setEmailError('That email does not match the one on this share. Contact the caregiver if you believe this is a mistake.')
      return
    }

    // Mark verified
    await supabase
      .from('shares')
      .update({ email_verified_at: new Date().toISOString() })
      .eq('id', share.id)

    setEmailVerified(true)
    setToast({ message: 'Email verified. You can now confirm or dispute shifts.', type: 'success' })
  }

  async function submitCorroboration(shiftId, state) {
    try {
      const { error: err } = await supabase
        .from('corroborations')
        .insert({
          shift_id: shiftId,
          share_id: share.id,
          corroborator_name: share.shared_with_name,
          state,
          note: responseNote || null,
          is_realtime: false,
          method: 'web',
        })

      if (err) throw err

      setRespondingTo(null)
      setResponseNote('')
      setToast({
        message: state === 'confirmed' ? 'Shift confirmed. Thank you.' :
                 state === 'disputed' ? 'Dispute recorded permanently.' :
                 'Response recorded.',
        type: state === 'confirmed' ? 'success' : state === 'disputed' ? 'error' : 'success'
      })
      loadShare()
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  function exportCSV() {
    const headers = ['Date', 'Clock in', 'Clock out', 'Duration hours', 'Type', 'Confidence', 'Tasks', 'Notes', 'Incident', 'Corroboration']
    const rows = shifts.map(s => {
      const hours = ((new Date(s.clock_out) - new Date(s.clock_in)) / 3600000).toFixed(2)
      const corrob = corroborations[s.id]?.[0]
      return [
        s.date,
        new Date(s.clock_in).toLocaleString(),
        new Date(s.clock_out).toLocaleString(),
        hours,
        s.shift_type,
        s.is_backfilled ? (CONFIDENCE_CSV_LABEL[s.confidence] || 'From memory') : '',
        (s.tasks || []).join('; '),
        (s.notes || '').replace(/"/g, '""'),
        s.incident ? 'YES' : '',
        corrob ? `${corrob.state} by ${corrob.corroborator_name}` : '',
      ]
    })

    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `kindredcare-shifts-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
    setToast({ message: 'CSV downloaded', type: 'success' })
  }

  // ── Error states ────────────────────────────────────
  if (loading) return <SharedLayout><div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#718096' }}>Loading...</div></SharedLayout>

  if (error === 'invalid') return <ErrorState icon={Lock} title="Share link not found" description="This link may be incorrect or has been removed. Contact the person who shared it with you." />
  if (error === 'revoked') return <ErrorState icon={Lock} title="Access revoked" description="The person who shared this link has revoked access. Contact them if you need continued access." />
  if (error === 'expired') return <ErrorState icon={Clock} title="Share link expired" description="This link has expired. Contact the person who shared it to request a new one." />

  const typeMeta = SHARE_TYPE_META[share.share_type] || SHARE_TYPE_META.verification
  const canCorroborate = share.permissions?.can_corroborate
  const canExport = share.permissions?.can_export
  const viewNotes = share.permissions?.view_notes
  const viewPay = share.permissions?.view_pay
  const scheduleOnly = share.permissions?.schedule_only

  // ── Email verification gate (for interactive shares) ────────
  const requiresVerification = (canCorroborate || canExport) && share.shared_with_email

  if (requiresVerification && !emailVerified) {
    return (
      <SharedLayout>
        <Card style={{ maxWidth: '440px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
            <div style={{ padding: '8px', background: '#E1F5EE', borderRadius: '8px' }}>
              <Shield size={20} style={{ color: '#005440' }} />
            </div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 500, color: '#1A202C' }}>
              Verify your email
            </h2>
          </div>
          <p style={{ color: '#4A5568', marginBottom: '1.5rem', lineHeight: 1.6, fontSize: '14px' }}>
            You've been given access to {caregiverProfile?.name || 'a caregiver'}'s care records with the ability to {canCorroborate ? 'confirm or dispute shifts' : 'export records'}. To make sure responses are tied to a real identity, please verify the email address the caregiver used when they shared this link with you.
          </p>
          <form onSubmit={verifyEmail}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }}>Your email</label>
              <input
                type="email"
                value={verifyingEmail}
                onChange={e => setVerifyingEmail(e.target.value)}
                placeholder="e.g. name@email.com"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '15px' }}
                autoFocus
                required
              />
            </div>
            {emailError && (
              <div style={{ padding: '10px 12px', background: '#FAECE7', color: '#993C1D', borderRadius: '6px', fontSize: '13px', marginBottom: '1rem' }}>
                {emailError}
              </div>
            )}
            <Button type="submit" style={{ width: '100%' }} disabled={!verifyingEmail.trim()}>Verify and continue</Button>
          </form>
        </Card>
      </SharedLayout>
    )
  }

  // ── Main shared view ────────────────────────────────
  const totalHours = shifts.reduce((sum, s) => sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000, 0)
  const TypeIcon = typeMeta.icon

  return (
    <SharedLayout>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '24px', fontWeight: 500, color: '#1A202C', marginBottom: '4px' }}>
          {scheduleOnly ? 'Care schedule' : 'Shared care record'}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', color: '#718096', fontSize: '14px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <TypeIcon size={14} /> {typeMeta.label}
          </span>
          <span>·</span>
          <span>Shared by {caregiverProfile?.name || 'caregiver'}</span>
          <span>·</span>
          <span>{shifts.length} shifts · {totalHours.toFixed(1)} total hours</span>
          {canCorroborate && (
            <>
              <span>·</span>
              <span style={{ color: '#2C7A7B', fontWeight: 500 }}>You can confirm or dispute shifts</span>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.5rem', padding: '10px 14px', background: '#E1F5EE', borderRadius: '8px', fontSize: '13px', color: '#005440' }}>
        <Shield size={15} />
        These records are tamper-evident. Shifts lock 30 minutes after clock-out and cannot be edited afterward.
      </div>

      {/* Export button for professional shares */}
      {canExport && (
        <div style={{ marginBottom: '1.5rem' }}>
          <Button variant="secondary" onClick={exportCSV}>
            <Download size={16} /> Export shifts as CSV
          </Button>
        </div>
      )}

      {/* Evidence notes section (oversight + professional) */}
      {viewNotes && notes.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
            Evidence notes ({notes.length})
          </h2>
          <div style={{ display: 'grid', gap: '8px' }}>
            {notes.slice(0, 5).map(note => (
              <Card key={note.id} style={{ borderLeft: '3px solid #2C7A7B' }}>
                <div style={{ fontSize: '13px', color: '#A0AEC0', marginBottom: '4px' }}>{formatDate(note.date)}</div>
                <div style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{note.text}</div>
              </Card>
            ))}
            {notes.length > 5 && <div style={{ fontSize: '13px', color: '#718096', textAlign: 'center' }}>+ {notes.length - 5} more notes</div>}
          </div>
        </div>
      )}

      {/* Pay info section (professional only) */}
      {viewPay && rates && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
            Pay rates
          </h2>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#4A5568', fontSize: '14px' }}>
              <DollarSign size={16} />
              {rates.mode === 'single' ? (
                <span>Default rate: <strong style={{ color: '#1A202C' }}>${rates.single_rate}/hr</strong></span>
              ) : (
                <span>Multiple rates by client — see shift breakdown</span>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Shifts section */}
      <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
        Shifts ({shifts.length})
      </h2>
      <div style={{ display: 'grid', gap: '10px' }}>
        {shifts.map(shift => {
          const shiftCorroborations = corroborations[shift.id] || []
          const lastResponse = shiftCorroborations[0]
          const isResponding = respondingTo === shift.id

          return (
            <Card key={shift.id} style={{
              borderLeft: shift.authorship === 'observed'
                ? '3px solid #8b5000'
                : shift.incident
                  ? '3px solid #E07856'
                  : shift.locked_at
                    ? '3px solid #2C7A7B'
                    : undefined,
              background: shift.authorship === 'observed' ? '#FDFCF7' : undefined,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: '10px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C' }}>{formatDate(shift.date)}</span>
                    {shift.authorship === 'observed' && (
                      <Badge variant="amber">Observed</Badge>
                    )}
                    <Badge>{shift.shift_type.replace(/_/g, ' ')}</Badge>
                    {shift.incident && !scheduleOnly && <Badge variant="coral">Incident</Badge>}
                    {shift.locked_at && <Badge variant="gray">Locked</Badge>}
                    {!scheduleOnly && shift.is_backfilled && shift.confidence && CONFIDENCE_MAP[shift.confidence] && (
                      <span style={{
                        fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '4px',
                        color: CONFIDENCE_MAP[shift.confidence].color,
                        background: CONFIDENCE_MAP[shift.confidence].bg,
                      }}>
                        {CONFIDENCE_MAP[shift.confidence].short}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: '#718096' }}>
                    {formatTime(shift.clock_in)} – {formatTime(shift.clock_out)} · {formatDuration(shift.clock_in, shift.clock_out)}
                  </div>
                  {shift.authorship === 'observed' && (
                    <div style={{ fontSize: '12px', color: '#8b5000', marginTop: '3px' }}>
                      Worked by {shift.observed_caregiver_name}
                      {shift.evidence_source && ` · Basis: ${shift.evidence_source.replace(/_/g, ' ')}`}
                    </div>
                  )}
                </div>

                {lastResponse && !scheduleOnly && (
                  <CorroborationBadge state={lastResponse.state} name={lastResponse.corroborator_name} />
                )}
              </div>

              {/* Notes and tasks hidden for schedule-only view */}
              {!scheduleOnly && shift.notes && (
                <div style={{ fontSize: '13px', color: '#4A5568', marginTop: '6px', fontStyle: 'italic', lineHeight: 1.5 }}>
                  {shift.notes}
                </div>
              )}

              {!scheduleOnly && shift.tasks?.length > 0 && (
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '8px' }}>
                  {shift.tasks.map(t => <Badge key={t} variant="gray">{t.replace(/_/g, ' ')}</Badge>)}
                </div>
              )}

              {/* Corroboration history (not shown in schedule-only) */}
              {!scheduleOnly && shiftCorroborations.length > 0 && (
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #EDF2F7' }}>
                  {shiftCorroborations.map(c => (
                    <div key={c.id} style={{ fontSize: '12px', color: '#718096', marginBottom: '4px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <CorroborationIcon state={c.state} />
                      <span>
                        <strong>{c.corroborator_name}</strong> marked as {c.state} ·{' '}
                        {new Date(c.responded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                      {c.note && <span style={{ fontStyle: 'italic' }}> — "{c.note}"</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Confirm/Dispute buttons (verification shares only) */}
              {canCorroborate && !isResponding && (
                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #EDF2F7', display: 'flex', gap: '8px' }}>
                  <Button size="sm" onClick={() => submitCorroboration(shift.id, 'confirmed')}>
                    <Check size={14} /> Confirm
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setRespondingTo(shift.id)}
                    style={{ color: '#993C1D', borderColor: '#FAECE7' }}>
                    <AlertTriangle size={14} /> Dispute
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => submitCorroboration(shift.id, 'unsure')}>
                    <HelpCircle size={14} /> Not sure
                  </Button>
                </div>
              )}

              {canCorroborate && isResponding && (
                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #EDF2F7' }}>
                  <p style={{ fontSize: '13px', color: '#4A5568', marginBottom: '8px' }}>
                    Your dispute will be recorded permanently. The shift will be flagged but not removed.
                  </p>
                  <Textarea
                    label="What's not right about this shift? (optional)"
                    id={`dispute-${shift.id}`}
                    value={responseNote}
                    onChange={e => setResponseNote(e.target.value)}
                    placeholder="e.g. I don't think this shift happened, or the times are incorrect."
                    style={{ minHeight: '60px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button variant="ghost" size="sm" onClick={() => { setRespondingTo(null); setResponseNote('') }}>Cancel</Button>
                    <Button variant="danger" size="sm" onClick={() => submitCorroboration(shift.id, 'disputed')}>
                      Submit dispute
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )
        })}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </SharedLayout>
  )
}

function SharedLayout({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#F7FAFC', padding: '2rem 1rem' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2rem' }}>
          <div style={{
            width: '32px', height: '32px', background: '#2C7A7B', borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#FFFFFF', fontFamily: "'Playfair Display', Georgia, serif", fontSize: '16px', fontWeight: 500,
          }}>K</div>
          <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17px', fontWeight: 500, color: '#1A202C' }}>
            KindredCare
          </span>
        </div>
        {children}
      </div>
    </div>
  )
}

function ErrorState({ icon: Icon, title, description }) {
  return (
    <SharedLayout>
      <Card style={{ textAlign: 'center', padding: '3rem 2rem' }}>
        <Icon size={40} style={{ color: '#E2E8F0', marginBottom: '1rem' }} />
        <h2 style={{ fontSize: '20px', fontWeight: 500, color: '#1A202C', marginBottom: '8px' }}>{title}</h2>
        <p style={{ color: '#718096', maxWidth: '400px', margin: '0 auto' }}>{description}</p>
      </Card>
    </SharedLayout>
  )
}

function CorroborationBadge({ state, name }) {
  const styles = {
    confirmed: { bg: '#E1F5EE', color: '#0F6E56', icon: Check, label: `Confirmed by ${name}` },
    disputed: { bg: '#FAECE7', color: '#993C1D', icon: AlertTriangle, label: `Disputed by ${name}` },
    unsure: { bg: '#FCF5E8', color: '#8b5000', icon: HelpCircle, label: `${name}: unsure` },
  }
  const s = styles[state] || styles.unsure
  const Icon = s.icon

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, background: s.bg, color: s.color }}>
      <Icon size={12} /> {s.label}
    </span>
  )
}

function CorroborationIcon({ state }) {
  if (state === 'confirmed') return <Check size={12} style={{ color: '#0F6E56' }} />
  if (state === 'disputed') return <AlertTriangle size={12} style={{ color: '#993C1D' }} />
  return <HelpCircle size={12} style={{ color: '#8b5000' }} />
}
